import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user, userProfile, signOut } = useAuth();

  const isTrainer = userProfile?.role === 'personal_trainer';
  const isClient = userProfile?.role === 'client';

  const trainerNavigationItems = [
    { 
      label: 'Dashboard', 
      path: '/dashboard', 
      icon: 'LayoutDashboard', 
      tooltip: 'Overview of your clients and activities'
    },
    { 
      label: 'Trainer Dashboard', 
      path: '/trainer-dashboard', 
      icon: 'Users', 
      tooltip: 'Client management and performance monitoring'
    },
    { 
      label: 'Client Assessment', 
      path: '/client-assessment', 
      icon: 'ClipboardList', 
      tooltip: 'Conduct comprehensive client intake evaluations'
    },
    { 
      label: 'Predictive Analytics', 
      path: '/predictive-analytics-dashboard', 
      icon: 'TrendingUp', 
      tooltip: 'AI-powered performance forecasting and client success prediction'
    },
    { 
      label: 'Device Integration', 
      path: '/device-integration-hub', 
      icon: 'Watch', 
      tooltip: 'Connect and manage wearable devices'
    },
    { 
      label: 'Data Sync', 
      path: '/real-time-data-sync', 
      icon: 'RefreshCw', 
      tooltip: 'Monitor real-time wearable data synchronization'
    },
    { 
      label: 'Performance Analytics', 
      path: '/performance-analytics', 
      icon: 'BarChart3', 
      tooltip: 'Advanced analytics and reporting'
    },
    { 
      label: 'Clients', 
      path: '/workout-management', 
      icon: 'Users', 
      tooltip: 'Manage your clients and assign workouts'
    },
    { 
      label: 'Workout Catalog', 
      path: '/workout-catalog', 
      icon: 'Dumbbell', 
      tooltip: 'Create and manage workout plans'
    },
    { 
      label: 'Workout Builder', 
      path: '/workout-builder', 
      icon: 'Wrench', 
      tooltip: 'Build custom workouts with drag-and-drop'
    },
    { 
      label: 'Plan Builder', 
      path: '/trainer-workout-builder', 
      icon: 'CalendarPlus', 
      tooltip: 'Build weekly workout programs per client'
    },
    { 
      label: 'Communication Hub', 
      path: '/communication-hub', 
      icon: 'MessageCircle', 
      tooltip: 'Real-time messaging with clients'
    },
    { 
      label: 'Reports', 
      path: '/progress-reports', 
      icon: 'TrendingUp', 
      tooltip: 'View client progress and analytics'
    },
    { 
      label: 'Pricing', 
      path: '/pricing', 
      icon: 'CreditCard', 
      tooltip: 'View and manage your subscription plan'
    }
  ];

  const clientNavigationItems = [
    { 
      label: 'Dashboard', 
      path: '/dashboard', 
      icon: 'LayoutDashboard', 
      tooltip: 'Overview of your wellness metrics'
    },
    { 
      label: 'My Workouts', 
      path: '/client-workout-dashboard', 
      icon: 'Dumbbell', 
      tooltip: 'View and complete your assigned workout program'
    },
    { 
      label: 'My Plans', 
      path: '/client-plans', 
      icon: 'ClipboardList', 
      tooltip: 'View your trainer-assigned workout & nutrition plans with adherence tracking'
    },
    { 
      label: 'Wearable Metrics', 
      path: '/client-wearable-metrics-dashboard', 
      icon: 'Watch', 
      tooltip: 'Live data from your Garmin, Apple Watch, Whoop & Polar devices'
    },
    { 
      label: 'Activity', 
      path: '/activity-tracking', 
      icon: 'Activity', 
      tooltip: 'Track your physical activities and steps'
    },
    { 
      label: 'Nutrition', 
      path: '/nutrition-tracking', 
      icon: 'Apple', 
      tooltip: 'Monitor your calorie intake and meals'
    },
    { 
      label: 'Progress', 
      path: '/progress-tracking', 
      icon: 'Camera', 
      tooltip: 'Track your progress with photos and metrics'
    },
    { 
      label: 'Communication Hub', 
      path: '/communication-hub', 
      icon: 'MessageCircle', 
      tooltip: 'Message your trainer in real-time'
    }
  ];

  const navigationItems = isTrainer ? trainerNavigationItems : clientNavigationItems;

  const secondaryItems = [
    { 
      label: 'Profile', 
      path: '/profile-settings', 
      icon: 'Settings', 
      tooltip: 'Manage your account and preferences'
    }
  ];

  const isActive = (path) => location?.pathname === path;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleProfileClick = () => {
    window.location.href = '/profile-settings';
  };

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/login';
  };

  return (
    <motion.header 
      className="sticky top-0 z-50 w-full border-b border-white/[0.06]"
      style={{ background: 'rgba(10,10,11,0.92)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <div className="flex h-16 items-center px-4 lg:px-6">
        <Link 
          to="/dashboard" 
          className="flex items-center space-x-3 mr-8 group focus-wellness"
        >
          <motion.div 
            className="flex items-center justify-center w-9 h-9 rounded-xl overflow-hidden"
            whileHover={{ scale: 1.05, rotate: 5 }}
            whileTap={{ scale: 0.95 }}
          >
            <img 
              src="/assets/images/Gemini_Generated_Image_f4m1kzf4m1kzf4m1-1774360718369.png"
              alt="FormForge logo"
              className="w-full h-full object-cover"
            />
          </motion.div>
          <div className="hidden sm:block">
            <span className="font-bold text-xl text-white tracking-tight" style={{ fontFamily: "'Inter Tight', sans-serif" }}>FormForge</span>
            <div className="text-xs -mt-1" style={{ color: '#6B7280' }}>{isTrainer ? 'Trainer Portal' : 'Client Portal'}</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center space-x-1 flex-1">
          {navigationItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`
                group relative flex items-center space-x-2 px-3 py-2 rounded-xl text-sm font-medium 
                transition-all duration-200 touch-target focus-wellness
                ${isActive(item?.path) 
                  ? 'text-white' :'hover:text-white'
                }
              `}
              style={isActive(item?.path) ? {
                background: 'rgba(255,107,0,0.15)',
                border: '1px solid rgba(255,107,0,0.3)',
                color: '#FF6B00',
                boxShadow: '0 0 12px rgba(255,107,0,0.15)'
              } : { color: '#6B7280', border: '1px solid transparent' }}
              title={item?.tooltip}
            >
              <Icon name={item?.icon} size={16} />
              <span className="text-constrained">{item?.label}</span>
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center space-x-3 ml-4 h-full">
          <motion.button
            onClick={handleProfileClick}
            className="flex items-center space-x-2 px-3 py-2 rounded-xl text-sm font-medium h-11 transition-all duration-300 touch-target group overflow-hidden"
            style={{ 
              background: 'rgba(255,255,255,0.05)', 
              border: '1px solid rgba(255,255,255,0.1)',
              width: '170px', minWidth: '170px', maxWidth: '170px'
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            title={`Signed in as ${userProfile?.fullName || user?.email}`}
          >
            <div className="relative flex-shrink-0">
              <div className="w-8 h-8 rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,107,0,0.4)' }}>
                {userProfile?.avatarUrl ? (
                  <img 
                    src={userProfile?.avatarUrl}
                    alt={userProfile?.fullName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white text-xs font-bold" style={{ background: 'linear-gradient(135deg, #FF6B00, #FF8C35)' }}>
                    {userProfile?.fullName?.charAt(0) || user?.email?.charAt(0)?.toUpperCase()}
                  </div>
                )}
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full pulse-green" style={{ border: '1px solid #0A0A0B' }}></div>
            </div>

            <div className="flex-1 min-w-0 text-left overflow-hidden max-w-[90px]">
              <div className="font-bold leading-tight text-sm overflow-hidden text-ellipsis whitespace-nowrap" style={{ color: '#E8EAED', fontFamily: "'Inter Tight', sans-serif" }}>
                {userProfile?.fullName?.length > 8 ? `${userProfile?.fullName?.substring(0, 8)}...` : userProfile?.fullName || 'User'}
              </div>
              <div className="text-xs leading-tight overflow-hidden text-ellipsis whitespace-nowrap" style={{ color: '#6B7280' }}>
                {isTrainer ? 'Trainer' : 'Client'}
              </div>
            </div>

            <Icon name="ChevronDown" size={14} className="flex-shrink-0 group-hover:rotate-180 transition-transform duration-300" style={{ color: '#6B7280' }} />
          </motion.button>

          <button
            onClick={handleSignOut}
            className="p-3 rounded-xl transition-all duration-300 touch-target h-11 flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#6B7280' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#FF4444'; e.currentTarget.style.borderColor = 'rgba(255,68,68,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#6B7280'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
            title="Sign Out"
          >
            <Icon name="LogOut" size={18} />
          </button>
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="md:hidden ml-auto rounded-xl focus-wellness"
          onClick={toggleMobileMenu}
        >
          <motion.div
            animate={{ rotate: isMobileMenuOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={20} />
          </motion.div>
        </Button>
      </div>
      <motion.div
        className="md:hidden border-t border-white/[0.06]"
        style={{ background: 'rgba(10,10,11,0.96)', backdropFilter: 'blur(20px)' }}
        initial={{ opacity: 0, height: 0 }}
        animate={{ 
          opacity: isMobileMenuOpen ? 1 : 0,
          height: isMobileMenuOpen ? 'auto' : 0 
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{ overflow: 'hidden', background: 'rgba(10,10,11,0.96)', backdropFilter: 'blur(20px)' }}
      >
        {isMobileMenuOpen && (
          <nav className="wellness-fade-in">
            <div className="p-4 border-b border-white/[0.06]">
              <motion.button
                onClick={handleProfileClick}
                className="flex items-center space-x-4 w-full p-3 rounded-xl text-sm font-medium transition-all duration-200 touch-target"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="relative flex-shrink-0">
                  <div className="w-12 h-12 rounded-full overflow-hidden" style={{ border: '1px solid rgba(255,107,0,0.3)' }}>
                    {userProfile?.avatarUrl ? (
                      <img src={userProfile?.avatarUrl} alt={userProfile?.fullName} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white text-sm font-bold" style={{ background: 'linear-gradient(135deg, #FF6B00, #FF8C35)' }}>
                        {userProfile?.fullName?.charAt(0) || user?.email?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full pulse-green" style={{ border: '2px solid #0A0A0B' }}></div>
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="font-medium truncate" style={{ color: '#E8EAED' }}>{userProfile?.fullName || user?.email}</div>
                  <div className="text-xs truncate" style={{ color: '#6B7280' }}>{userProfile?.role}</div>
                  <div className="text-xs mt-1" style={{ color: '#AAFF00' }}>View Profile Settings</div>
                </div>
                <Icon name="ChevronRight" size={16} style={{ color: '#6B7280' }} />
              </motion.button>
            </div>

            <div className="p-4 space-y-1">
              <div className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: '#6B7280' }}>Navigation</div>
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center space-x-3 p-3 rounded-xl text-sm font-medium transition-all duration-200 touch-target"
                  style={isActive(item?.path) ? {
                    background: 'rgba(255,107,0,0.12)',
                    border: '1px solid rgba(255,107,0,0.25)',
                    color: '#FF6B00'
                  } : { color: '#6B7280', border: '1px solid transparent' }}
                >
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg" style={{ background: isActive(item?.path) ? 'rgba(255,107,0,0.15)' : 'rgba(255,255,255,0.04)' }}>
                    <Icon name={item?.icon} size={18} />
                  </div>
                  <span className="text-constrained flex-1">{item?.label}</span>
                  {isActive(item?.path) && <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#FF6B00' }} />}
                </Link>
              ))}
            </div>
            
            <div className="border-t border-white/[0.06] p-4">
              <div className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: '#6B7280' }}>Account</div>
              {secondaryItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center space-x-3 p-3 rounded-xl text-sm font-medium transition-all duration-200 touch-target"
                  style={isActive(item?.path) ? {
                    background: 'rgba(170,255,0,0.1)',
                    border: '1px solid rgba(170,255,0,0.25)',
                    color: '#AAFF00'
                  } : { color: '#6B7280', border: '1px solid transparent' }}
                >
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
                    <Icon name={item?.icon} size={18} />
                  </div>
                  <span className="text-constrained flex-1">{item?.label}</span>
                </Link>
              ))}
              <button
                onClick={handleSignOut}
                className="flex items-center space-x-3 p-3 rounded-xl text-sm font-medium transition-all duration-200 touch-target w-full mt-2"
                style={{ color: '#FF4444', border: '1px solid rgba(255,68,68,0.2)', background: 'rgba(255,68,68,0.05)' }}
              >
                <div className="flex items-center justify-center w-9 h-9 rounded-lg" style={{ background: 'rgba(255,68,68,0.1)' }}>
                  <Icon name="LogOut" size={18} />
                </div>
                <span>Sign Out</span>
              </button>
            </div>
          </nav>
        )}
      </motion.div>
    </motion.header>
  );
};

export default Header;